import { useState, useEffect } from "react";
import { api } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Users, DollarSign, Percent, TrendingUp } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const PartnersPage = () => {
  const [partners, setPartners] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    partner_name: "",
    profit_share_type: "fixed",
    fixed_percentage: "",
    tiered_rules: [{ min_profit: "", max_profit: "", percentage: "" }],
    projects: [],
    status: "active",
  });
  
  const [availableProjects, setAvailableProjects] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [partnersRes, payoutsRes, projectsRes] = await Promise.all([
        api.get("/partners"),
        api.get("/financials/partners/payouts"),
        api.get("/projects"),
      ]);
      setPartners(partnersRes.data);
      setPayouts(payoutsRes.data);
      setAvailableProjects(projectsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch partner data");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (partner = null) => {
    if (partner) {
      setEditingPartner(partner);
      setFormData({
        partner_name: partner.partner_name,
        profit_share_type: partner.profit_share_type,
        fixed_percentage: partner.fixed_percentage?.toString() || "",
        tiered_rules: partner.tiered_rules || [{ min_profit: "", max_profit: "", percentage: "" }],
        projects: partner.projects || [],
        status: partner.status,
      });
    } else {
      setEditingPartner(null);
      setFormData({
        partner_name: "",
        profit_share_type: "fixed",
        fixed_percentage: "",
        tiered_rules: [{ min_profit: "", max_profit: "", percentage: "" }],
        projects: [],
        status: "active",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPartner(null);
  };

  const addTieredRule = () => {
    setFormData({
      ...formData,
      tiered_rules: [...formData.tiered_rules, { min_profit: "", max_profit: "", percentage: "" }]
    });
  };

  const removeTieredRule = (index) => {
    const newRules = formData.tiered_rules.filter((_, i) => i !== index);
    setFormData({ ...formData, tiered_rules: newRules });
  };

  const updateTieredRule = (index, field, value) => {
    const newRules = [...formData.tiered_rules];
    newRules[index][field] = value;
    setFormData({ ...formData, tiered_rules: newRules });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        partner_name: formData.partner_name,
        profit_share_type: formData.profit_share_type,
        projects: formData.projects,
        status: formData.status,
      };

      if (formData.profit_share_type === "fixed") {
        payload.fixed_percentage = parseFloat(formData.fixed_percentage);
      } else {
        payload.tiered_rules = formData.tiered_rules.map(rule => ({
          min_profit: parseFloat(rule.min_profit),
          max_profit: rule.max_profit ? parseFloat(rule.max_profit) : null,
          percentage: parseFloat(rule.percentage),
        }));
      }

      if (editingPartner) {
        await api.put(`/partners/${editingPartner.id}`, payload);
        toast.success("Partner updated successfully");
      } else {
        await api.post("/partners", payload);
        toast.success("Partner created successfully");
      }

      fetchData();
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving partner:", error);
      toast.error("Failed to save partner");
    }
  };
  
  const toggleProject = (projectName) => {
    const currentProjects = [...formData.projects];
    const index = currentProjects.indexOf(projectName);
    
    if (index > -1) {
      currentProjects.splice(index, 1);
    } else {
      currentProjects.push(projectName);
    }
    
    setFormData({ ...formData, projects: currentProjects });
  };

  const handleDelete = async (partnerId) => {
    if (!window.confirm("Are you sure you want to delete this partner?")) {
      return;
    }

    try {
      await api.delete(`/partners/${partnerId}`);
      toast.success("Partner deleted successfully");
      fetchData();
    } catch (error) {
      console.error("Error deleting partner:", error);
      toast.error("Failed to delete partner");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const totalPayouts = payouts.reduce((sum, p) => sum + p.payout_amount, 0);
  const totalProfit = payouts.length > 0 ? payouts[0].total_profit : 0;
  const remainingProfit = totalProfit - totalPayouts;

  return (
    <div className="space-y-6 page-enter" data-testid="partners-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Partner Management</h1>
          <p className="text-slate-600 mt-1">Manage profit-sharing partners and payouts</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-blue-600 hover:bg-blue-700 text-white shadow-md"
          data-testid="add-partner-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Partner
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Active Partners</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {partners.filter(p => p.status === "active").length}
                </p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Profit</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  ${totalProfit.toLocaleString()}
                </p>
              </div>
              <div className="bg-emerald-50 p-3 rounded-lg">
                <TrendingUp className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Partner Payouts</p>
                <p className="text-2xl font-bold text-orange-600 mt-1">
                  ${totalPayouts.toLocaleString()}
                </p>
              </div>
              <div className="bg-orange-50 p-3 rounded-lg">
                <DollarSign className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Your Share</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">
                  ${remainingProfit.toLocaleString()}
                </p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg">
                <Percent className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Partner Payouts Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle>Partner Payouts</CardTitle>
          <CardDescription>Current profit distribution based on partner agreements</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Partner Name</TableHead>
                <TableHead>Share Type</TableHead>
                <TableHead>Percentage</TableHead>
                <TableHead>Payout Amount</TableHead>
                <TableHead>Based on Profit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    No active partners with payouts
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {payouts.map((payout, index) => (
                    <TableRow key={index} data-testid={`payout-row-${index}`}>
                      <TableCell className="font-medium">{payout.partner_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          payout.profit_share_type === "fixed"
                            ? "border-blue-300 bg-blue-50 text-blue-700"
                            : "border-purple-300 bg-purple-50 text-purple-700"
                        }>
                          {payout.profit_share_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-semibold text-slate-900">
                        {payout.percentage_applied}%
                      </TableCell>
                      <TableCell className="text-orange-600 font-semibold text-lg">
                        ${payout.payout_amount.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        ${payout.total_profit.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-50 font-semibold">
                    <TableCell>Your Share (Remaining)</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>
                      {totalProfit > 0 ? ((remainingProfit / totalProfit) * 100).toFixed(1) : 0}%
                    </TableCell>
                    <TableCell className="text-emerald-600 text-lg">
                      ${remainingProfit.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      ${totalProfit.toLocaleString()}
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Partners Management Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle>All Partners</CardTitle>
          <CardDescription>Manage partner profit-sharing agreements</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Partner Name</TableHead>
                <TableHead>Share Type</TableHead>
                <TableHead>Configuration</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {partners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    No partners found. Add your first partner to get started.
                  </TableCell>
                </TableRow>
              ) : (
                partners.map((partner) => (
                  <TableRow key={partner.id} data-testid={`partner-row-${partner.id}`}>
                    <TableCell className="font-medium">{partner.partner_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        partner.profit_share_type === "fixed"
                          ? "border-blue-300 bg-blue-50 text-blue-700"
                          : "border-purple-300 bg-purple-50 text-purple-700"
                      }>
                        {partner.profit_share_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="space-y-2">
                        <div>
                          {partner.profit_share_type === "fixed" ? (
                            <span className="font-semibold text-slate-900">
                              {partner.fixed_percentage}% fixed
                            </span>
                          ) : (
                            <div className="space-y-1">
                              {partner.tiered_rules?.map((rule, idx) => (
                                <div key={idx} className="text-xs text-slate-600">
                                  ${rule.min_profit.toLocaleString()}-
                                  {rule.max_profit ? `$${rule.max_profit.toLocaleString()}` : "∞"}: {rule.percentage}%
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div>
                          {partner.projects && partner.projects.length > 0 ? (
                            <div className="text-xs text-slate-600 mt-1">
                              <span className="font-medium">Projects: </span>
                              {partner.projects.join(", ")}
                            </div>
                          ) : (
                            <div className="text-xs text-slate-500 italic mt-1">
                              All projects
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        partner.status === "active"
                          ? "border-green-300 bg-green-50 text-green-700"
                          : "border-slate-300 bg-slate-50 text-slate-700"
                      }>
                        {partner.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(partner)}
                          data-testid={`edit-partner-${partner.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(partner.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-partner-${partner.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Partner Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="partner-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingPartner ? "Edit Partner" : "Add New Partner"}
            </DialogTitle>
            <DialogDescription>
              Configure profit-sharing agreement for partner
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="partner_name">Partner Name *</Label>
                <Input
                  id="partner_name"
                  value={formData.partner_name}
                  onChange={(e) => setFormData({ ...formData, partner_name: e.target.value })}
                  required
                  data-testid="partner-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="profit_share_type">Profit Share Type *</Label>
                <Select
                  value={formData.profit_share_type}
                  onValueChange={(value) => setFormData({ ...formData, profit_share_type: value })}
                >
                  <SelectTrigger data-testid="profit-share-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixed Percentage</SelectItem>
                    <SelectItem value="tiered">Tiered (Based on Profit Levels)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.profit_share_type === "fixed" ? (
                <div className="space-y-2">
                  <Label htmlFor="fixed_percentage">Fixed Percentage (%) *</Label>
                  <Input
                    id="fixed_percentage"
                    type="number"
                    step="0.1"
                    value={formData.fixed_percentage}
                    onChange={(e) => setFormData({ ...formData, fixed_percentage: e.target.value })}
                    required
                    data-testid="fixed-percentage-input"
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  <Label>Tiered Rules</Label>
                  {formData.tiered_rules.map((rule, index) => (
                    <div key={index} className="flex gap-2 items-end border border-slate-200 p-3 rounded-lg">
                      <div className="flex-1 space-y-1">
                        <Label className="text-xs">Min Profit ($)</Label>
                        <Input
                          type="number"
                          value={rule.min_profit}
                          onChange={(e) => updateTieredRule(index, 'min_profit', e.target.value)}
                          required
                          data-testid={`tier-min-${index}`}
                        />
                      </div>
                      <div className="flex-1 space-y-1">
                        <Label className="text-xs">Max Profit ($)</Label>
                        <Input
                          type="number"
                          value={rule.max_profit}
                          onChange={(e) => updateTieredRule(index, 'max_profit', e.target.value)}
                          placeholder="Leave empty for ∞"
                          data-testid={`tier-max-${index}`}
                        />
                      </div>
                      <div className="flex-1 space-y-1">
                        <Label className="text-xs">Percentage (%)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={rule.percentage}
                          onChange={(e) => updateTieredRule(index, 'percentage', e.target.value)}
                          required
                          data-testid={`tier-percentage-${index}`}
                        />
                      </div>
                      {formData.tiered_rules.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeTieredRule(index)}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addTieredRule}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Tier
                  </Button>
                </div>
              )}

              <div className="space-y-2">
                <Label>Projects to Share Profit On *</Label>
                <div className="border border-slate-200 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                  {availableProjects.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-2">
                      No projects available. Add projects first.
                    </p>
                  ) : (
                    <>
                      <div className="flex items-center space-x-2 pb-2 border-b border-slate-200">
                        <Checkbox
                          id="all-projects"
                          checked={formData.projects.length === 0}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({ ...formData, projects: [] });
                            }
                          }}
                          data-testid="all-projects-checkbox"
                        />
                        <label
                          htmlFor="all-projects"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          All Projects (Default)
                        </label>
                      </div>
                      {availableProjects.map((project) => (
                        <div key={project.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`project-${project.id}`}
                            checked={formData.projects.includes(project.project_name)}
                            onCheckedChange={() => toggleProject(project.project_name)}
                            data-testid={`project-checkbox-${project.id}`}
                          />
                          <label
                            htmlFor={`project-${project.id}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            {project.project_name}
                          </label>
                        </div>
                      ))}
                    </>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Select specific projects or leave empty for all projects
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status *</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="partner-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                data-testid="partner-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="partner-save-button"
              >
                {editingPartner ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnersPage;
